/**
 * LaGift — cart
 * ---------------------------------------------------------------------------
 * The cart page works completely without this file. It is one real form: the
 * quantities post as `updates[]`, every gift field posts as `attributes[...]`,
 * and "Check out" saves all of it in the same request that starts the checkout.
 * The handwritten card is a second form joined to its button by the `form`
 * attribute, so even adding a product needs no script.
 *
 * What this adds is that none of it costs a page load, and that the gift data is
 * saved as it is typed rather than only when the customer finally presses the
 * button. That second part matters here more than on a normal store: someone
 * who writes a message, wanders off to check a spelling and comes back should
 * find their message still there.
 *
 * ---------------------------------------------------------------------------
 * HOW A CHANGE TRAVELS
 * ---------------------------------------------------------------------------
 * Ask Shopify to change the cart, and ask it in the same request to re-render
 * this section. Shopify returns the section's HTML built against the new cart,
 * so the prices, the totals, the quantity ceilings and the add-on's state all
 * arrive already correct instead of being patched up here one at a time.
 *
 * Only three regions are swapped in: the items, the summary, and the add-on
 * row. Deliberately not the gift fields - replacing a textarea somebody is
 * typing into is how you lose their message.
 */
(() => {
  // Matches `truncatewords: 6` in lagift-gift-block.liquid. Change both.
  const PREVIEW_WORDS = 6;

  const SECTION_TIMEOUT = 12000;

  class LagiftCart extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.form = this.querySelector('.lagift-cart__form');
      this.saveTimer = null;
      this.pending = 0;

      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('input', this.onInput.bind(this));
      this.addEventListener('click', this.onClick.bind(this));
      this.form?.addEventListener('submit', this.onSubmit.bind(this));

      this.syncCounters();
      this.syncCardText();
      this.revealCardDesign();
    }

    get cardDesignsRow() {
      return this.querySelector('[data-gift-card-designs-row]');
    }

    /**
     * Keep the chosen card design on screen.
     *
     * The designs are one sideways row and the add-on is swapped whole on every
     * cart change, so the row comes back at its start: pick the ninth design
     * with a card already in the cart and the page answers by showing the first
     * seven, none of them selected. `scrollLeft` is the row's position before
     * the swap, put back first so the row does not jump for a change that had
     * nothing to do with it.
     *
     * Computed rather than `scrollIntoView`, which scrolls every ancestor as
     * well and would move the page.
     */
    revealCardDesign(scrollLeft) {
      const row = this.cardDesignsRow;
      if (!row) return;
      if (scrollLeft) row.scrollLeft = scrollLeft;

      const tile = row.querySelector('[data-gift-card-design]:checked')?.parentElement;
      if (!tile) return;

      // Clear of the fade at the row's edge, not just inside the row.
      const margin = 32;
      const bounds = row.getBoundingClientRect();
      const box = tile.getBoundingClientRect();
      if (box.left < bounds.left + margin) row.scrollLeft -= bounds.left + margin - box.left;
      else if (box.right > bounds.right - margin) row.scrollLeft += box.right - (bounds.right - margin);
    }

    /**
     * Looked up each time rather than held: it sits inside the summary, and the
     * summary is one of the regions replaced on every cart change. A reference
     * taken at startup would point at a detached node from the second update on,
     * and errors would then be written into nothing at all.
     */
    get errorTarget() {
      return this.querySelector('[data-cart-error]');
    }

    /* ----------------------------------------------------------------------
     * Events
     * -------------------------------------------------------------------- */

    onChange(event) {
      const target = event.target;

      if (target.matches('[data-quantity-input]')) {
        this.changeQuantity(target);
        return;
      }

      if (target.matches('[data-gift-card-design]')) {
        this.changeCardDesign(target);
        return;
      }

      // Attributes and the order note are saved on blur as well as while
      // typing, so the last few characters are never left behind by the
      // debounce when someone tabs straight to the checkout button.
      if (this.isCartData(target)) {
        this.saveCartData();
        this.syncCardLineProperty();
      }
    }

    onInput(event) {
      const target = event.target;

      if (target.matches('[data-gift-message]')) {
        this.syncCounters();
        this.syncCardText();
      }

      if (this.isCartData(target)) {
        clearTimeout(this.saveTimer);
        this.saveTimer = setTimeout(() => this.saveCartData(), 600);
      }
    }

    onClick(event) {
      const remove = event.target.closest('[data-cart-item-remove], [data-gift-addon-remove]');
      if (remove) {
        // The href is Shopify's own change-quantity-to-zero URL, so with no
        // script it is simply a link that works.
        event.preventDefault();
        const row = remove.closest('[data-cart-item-key]');
        const key = row ? row.dataset.cartItemKey : this.keyFromRemoveUrl(remove.getAttribute('href'));
        if (key) this.updateLine({ id: key, quantity: 0 });
        return;
      }

      const add = event.target.closest('[data-gift-addon-add]');
      if (add) {
        event.preventDefault();
        this.addCard(add);
      }
    }

    onSubmit(event) {
      // Checkout is the last chance to catch a date that cannot be honoured.
      // The page re-checks it on every render as well - see the block's Liquid -
      // because a field is trivial to get around and a promise about a date is
      // not one to break.
      const problem = this.validateDate();
      if (problem) {
        event.preventDefault();
        this.showError(problem.message);
        problem.field.focus();
        return;
      }

      const checkoutUrl = this.checkoutUrlWithRecipient();
      const pending = this.cardSync;

      // Nothing to wait for and nowhere else to go: let the form post itself,
      // which is exactly what happens with no script at all.
      if (!checkoutUrl && !pending) return;

      event.preventDefault();
      // Save first, so the message, the date and the words on the card are all
      // on the cart no matter what happens next, then hand over.
      Promise.allSettled([this.saveCartData(), pending]).then(() => {
        if (checkoutUrl) {
          window.location.assign(checkoutUrl);
          return;
        }
        // `submit()` does not fire this handler again, so there is no loop.
        this.form.submit();
      });
    }

    /**
     * Carry the recipient into checkout so nobody types an address twice.
     *
     * Cart attributes reach the order, but they are not the shipping address and
     * checkout does not show them - verified against the live checkout, which
     * asked for the whole address again with every field empty. Shopify does
     * honour `checkout[shipping_address][...]`, which was verified the same way:
     * the fields arrived filled in and the shipping rate resolved.
     *
     * Returns null when there is nothing to carry, and the form then submits
     * normally. That is also what happens with no JavaScript at all, so the
     * worst case is exactly today's behaviour rather than a broken checkout.
     */
    checkoutUrlWithRecipient() {
      if (!this.hasAttribute('data-prefill-checkout')) return null;

      const value = (name) => {
        const field = this.querySelector(`[name="attributes[${name}]"]`);
        return field && field.value ? field.value.trim() : '';
      };

      const street = value('Delivery address');
      const name = value('Deliver to');
      if (!street || !name) return null;

      // Checkout wants the name in two halves. Everything after the first space
      // is the surname, so "Sanne de Vries" keeps the "de".
      const cut = name.indexOf(' ');
      const first = cut === -1 ? name : name.slice(0, cut);
      const last = cut === -1 ? '' : name.slice(cut + 1);

      const fields = {
        first_name: first,
        last_name: last,
        address1: street,
        zip: value('Delivery postcode'),
        city: value('Delivery city'),
        country: value('Delivery country'),
        phone: value('Delivery phone'),
      };

      const params = new URLSearchParams();
      Object.entries(fields).forEach(([key, entry]) => {
        if (entry) params.set(`checkout[shipping_address][${key}]`, entry);
      });
      if (![...params].length) return null;

      return `${window.Shopify.routes.root}checkout?${params.toString()}`;
    }

    /* ----------------------------------------------------------------------
     * Cart mutations
     * -------------------------------------------------------------------- */

    changeQuantity(input) {
      const row = input.closest('[data-cart-item-key]');
      if (!row) return;

      const quantity = Number(input.value);
      if (!Number.isFinite(quantity) || quantity < 0) return;

      this.updateLine({ id: row.dataset.cartItemKey, quantity }, row);
    }

    async updateLine(body, row) {
      this.setBusy(true);
      this.clearErrors();

      try {
        const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ ...body, ...this.sectionRequest() }),
          signal: AbortSignal.timeout(SECTION_TIMEOUT),
        });

        const data = await response.json();

        if (!response.ok) {
          // 422 is Shopify refusing, and its `description` is the sentence a
          // customer needs: "you can only add 1 of this to your cart". The
          // catalogue holds one unit of most things, so this is a normal event
          // here rather than an edge case - which is exactly why it has to be
          // readable.
          //
          // It must NOT reload. Reloading straight after writing the message
          // wipes it before anyone can read it: the number springs back and the
          // page never says why, which is the most annoying possible version of
          // being told no. Re-read the section instead, so the quantities come
          // from the server, then put the message back on the row it belongs to.
          const message = data.description || data.message;
          await this.refresh();
          this.showError(message, this.querySelector(`[data-cart-item-key="${body.id}"]`));
          return;
        }

        this.render(data);
      } catch (error) {
        // A failed request must not leave a cart showing numbers that are not
        // real. Falling back to a plain reload is slower and always honest.
        this.reload();
      } finally {
        this.setBusy(false);
      }
    }

    async addCard(button) {
      const addonForm = document.getElementById(button.getAttribute('form'));
      if (!addonForm) return;

      this.setBusy(true);
      this.clearErrors();
      this.syncCardText();

      try {
        const body = new FormData(addonForm);
        // `return_to` belongs to the browser's own form post, and sending it to
        // the AJAX endpoint makes Shopify reject the whole request.
        body.delete('return_to');
        this.sectionFormFields(body);

        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body,
          signal: AbortSignal.timeout(SECTION_TIMEOUT),
        });

        const data = await response.json();

        if (!response.ok) {
          this.showError(data.description || data.message);
          return;
        }

        this.render(data);
      } catch (error) {
        this.reload();
      } finally {
        this.setBusy(false);
      }
    }

    /**
     * A different card design, with one already in the basket.
     *
     * The radios are named `id` and belong to the add-to-cart form, so with no
     * script at all picking a design and pressing add posts the right variant.
     * This handles the other case: the card is already in the cart and the
     * customer changes their mind.
     *
     * It is a remove and an add, because `/cart/change.js` can move a quantity
     * and cannot move a line to a different variant. The properties are carried
     * across so the words written on the card survive the swap - losing them
     * here would be the customer retyping their message for choosing a
     * different picture.
     */
    async changeCardDesign(input) {
      const addon = this.querySelector('[data-gift-addon-line]');
      if (!addon) return; // nothing in the basket yet; the radio is enough

      const key = addon.dataset.giftAddonLine;
      const id = input.value;
      if (!key || !id) return;

      this.setBusy(true);
      this.clearErrors();

      try {
        const properties = {};
        const carrier = document.querySelector('[data-gift-card-text]');
        if (carrier && carrier.name) {
          const match = carrier.name.match(/^properties\[(.+)\]$/);
          if (match) properties[match[1]] = carrier.value || '';
        }

        const removed = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 }),
          signal: AbortSignal.timeout(SECTION_TIMEOUT),
        });
        if (!removed.ok) return this.refresh();

        const added = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: Number(id), quantity: 1, properties }),
          signal: AbortSignal.timeout(SECTION_TIMEOUT),
        });

        const data = await added.json();
        if (!added.ok) {
          // The old line is already gone, so the page must be told the truth
          // rather than left showing a card that is no longer in the basket.
          await this.refresh();
          this.showError(data.description || data.message);
          return;
        }

        await this.refresh();
      } catch (error) {
        this.reload();
      } finally {
        this.setBusy(false);
      }
    }

    /**
     * The gift data. Everything named `attributes[...]` plus the order note, in
     * one request, so the cart always holds a complete set rather than whichever
     * fields happened to fire last.
     */
    async saveCartData() {
      if (!this.form) return;

      const attributes = {};
      let note = null;

      new FormData(this.form).forEach((value, name) => {
        const match = name.match(/^attributes\[(.+)\]$/);
        if (match) attributes[match[1]] = value;
        if (name === 'note') note = value;
      });

      const payload = { attributes };
      if (note !== null) payload.note = note;

      try {
        await fetch(`${window.Shopify.routes.root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
          signal: AbortSignal.timeout(SECTION_TIMEOUT),
        });
      } catch (error) {
        // Silent on purpose. The same fields post with the form, so a failure
        // here costs the customer nothing at checkout - and an error banner over
        // a message they are still writing would be alarming and wrong.
      }
    }

    /**
     * Keep the card's own line reading what the message field says now.
     *
     * The message lives on the order as an attribute, which is always current.
     * The copy on the card's line is what whoever writes the card actually
     * looks at, so letting it go stale is a real mistake, not a cosmetic one.
     */
    async syncCardLineProperty() {
      const addon = this.querySelector('[data-gift-addon-line]');
      const message = this.querySelector('[data-gift-message]');
      const carrier = document.querySelector('[data-gift-card-text]');
      if (!addon || !message || !carrier) return;

      const propertyName = (carrier.name.match(/^properties\[(.+)\]$/) || [])[1];
      if (!propertyName) return;
      if (addon.dataset.giftAddonText === message.value) return;

      // Changing a line's properties gives it a new key, so the row has to come
      // back from Shopify rather than be patched in place.
      //
      // Held on the element so `onSubmit` can wait for it. This is a request to
      // the server, and a checkout that leaves before it lands hands the packer
      // words the customer had already deleted.
      this.cardSync = this.updateLine({
        id: addon.dataset.giftAddonLine,
        quantity: 1,
        properties: { [propertyName]: message.value },
      });

      try {
        await this.cardSync;
      } finally {
        this.cardSync = null;
      }
    }

    /* ----------------------------------------------------------------------
     * Rendering
     * -------------------------------------------------------------------- */

    sectionRequest() {
      return {
        sections: [this.sectionId, 'cart-icon-bubble'].filter(Boolean),
        sections_url: window.location.pathname,
      };
    }

    sectionFormFields(body) {
      body.append('sections', [this.sectionId, 'cart-icon-bubble'].filter(Boolean).join(','));
      body.append('sections_url', window.location.pathname);
      return body;
    }

    /**
     * Re-read this section from the server and swap the same regions a change
     * would have. Used when a change was refused: the cart did not move, but the
     * page is now showing a number the server never accepted.
     */
    async refresh() {
      try {
        const url = `${window.location.pathname}?section_id=${encodeURIComponent(this.sectionId)}`;
        const response = await fetch(url, { signal: AbortSignal.timeout(SECTION_TIMEOUT) });
        if (!response.ok) return;
        this.render({ sections: { [this.sectionId]: await response.text() } });
      } catch (error) {
        // Leave the page as it is. The message still gets shown, and the worst
        // case is a number that a real reload would correct.
      }
    }

    render(data) {
      const html = data.sections && data.sections[this.sectionId];
      if (!html) return this.reload();

      const parsed = new DOMParser().parseFromString(html, 'text/html');

      // An emptied cart renders a different page entirely - there is no items
      // region to swap into - so let the browser fetch it properly.
      if (!parsed.querySelector('[data-cart-items]')) return this.reload();

      const focus = this.rememberFocus();
      const designsScroll = this.cardDesignsRow?.scrollLeft;

      this.swap(parsed, '[data-cart-items]');
      this.swap(parsed, '.lagift-cart__summary-inner');
      this.swap(parsed, '.lagift-cart__count');
      this.swap(parsed, '[data-gift-addon]');

      this.restoreFocus(focus);
      this.revealCardDesign(designsScroll);
      this.syncCardText();
      this.syncCardGating();
      this.updateCartIcon(data);
    }

    swap(parsed, selector) {
      const next = parsed.querySelector(selector);
      const current = this.querySelector(selector);
      if (next && current) current.replaceWith(next);
    }

    updateCartIcon(data) {
      const html = data.sections && data.sections['cart-icon-bubble'];
      const bubble = document.getElementById('cart-icon-bubble');
      if (!html || !bubble) return;

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const next = parsed.querySelector('.shopify-section');
      if (next) bubble.innerHTML = next.innerHTML;
    }

    /**
     * Focus survives a swap by name and value rather than by element, because
     * the element it was on no longer exists. Losing it would drop a keyboard
     * user back at the top of the document every time they pressed "plus".
     */
    rememberFocus() {
      const active = document.activeElement;
      if (!active || !this.contains(active)) return null;

      const row = active.closest('[data-cart-item-key]');
      return {
        key: row ? row.dataset.cartItemKey : null,
        selector: active.matches('[data-quantity-input]')
          ? '[data-quantity-input]'
          : active.dataset.quantityStep
            ? `[data-quantity-step="${active.dataset.quantityStep}"]`
            : null,
      };
    }

    restoreFocus(focus) {
      if (!focus || !focus.selector) return;

      const scope = focus.key
        ? this.querySelector(`[data-cart-item-key="${focus.key}"]`)
        : this;
      const target = scope && scope.querySelector(focus.selector);

      // A step button that has become disabled - the last unit removed, say -
      // cannot take focus, so fall back to the field it belongs to.
      if (target && !target.disabled) target.focus();
      else scope?.querySelector('[data-quantity-input]')?.focus();
    }

    reload() {
      window.location.reload();
    }

    /* ----------------------------------------------------------------------
     * Small helpers
     * -------------------------------------------------------------------- */

    isCartData(element) {
      return element.name === 'note' || /^attributes\[/.test(element.name || '');
    }

    /** `/cart/change?line=2&quantity=0` and `/cart/change?id=<key>&quantity=0`. */
    keyFromRemoveUrl(href) {
      if (!href) return null;
      try {
        return new URL(href, window.location.origin).searchParams.get('id');
      } catch (error) {
        return null;
      }
    }

    validateDate() {
      const field = this.querySelector('[data-gift-date]');
      if (!field || !field.value || !field.min) return null;
      if (field.value >= field.min) return null;

      // Ours first, the one Liquid already rendered second, the browser's own
      // string only if both are somehow missing.
      const rendered = field.closest('.lagift-field')?.querySelector('.lagift-field__error');
      return {
        field,
        message:
          field.dataset.giftDateMessage ||
          (rendered ? rendered.textContent.trim() : field.validationMessage),
      };
    }

    syncCounters() {
      this.querySelectorAll('[data-gift-counter]').forEach((counter) => {
        const field = counter.closest('.lagift-gift-block__body')?.querySelector('[data-gift-message]');
        const max = Number(counter.dataset.giftCounterMax);
        if (!field || !Number.isFinite(max)) return;

        const used = field.value.length;
        counter.textContent = `${used} / ${max}`;
        counter.classList.toggle('is-near-limit', used > max * 0.9);
      });
    }

    /**
     * The hidden field the add-to-cart form will carry. Kept in step as the
     * message is typed so the card is added with the text already on it, rather
     * than needing a second request afterwards.
     */
    syncCardText() {
      const message = this.querySelector('[data-gift-message]');
      const carrier = document.querySelector('[data-gift-card-text]');
      if (message && carrier) carrier.value = message.value;
      this.syncCardNote(message);
    }

    /**
     * Reveal anything the merchant has made wait for the card.
     *
     * Liquid gets this right on a full page load; this is for the swap after
     * the card is added or removed, which does not re-render the gift panel.
     * Toggling `hidden` rather than rebuilding the field is deliberate - the
     * customer may already have typed something, and rebuilding loses it.
     *
     * The card's presence is read off the addon tile, which the swap does
     * replace, so there is nothing else to keep in step.
     */
    syncCardGating() {
      const gated = this.querySelectorAll('[data-gift-requires-card]');
      if (!gated.length) return;

      const added = !!this.querySelector('[data-gift-addon-line]');
      gated.forEach((el) => {
        el.hidden = !added;
      });
    }

    /**
     * Show, on the card itself, the words we are going to write.
     *
     * The card borrows the message field rather than growing a second box, so
     * nothing on the card said what it was carrying. Updated as the customer
     * types, because the whole point is that they can see their own sentence
     * on the thing they just paid for.
     */
    syncCardNote(message) {
      const note = this.querySelector('[data-gift-card-note-text]');
      if (!note) return;

      const text = (message ? message.value : '').trim();
      note.textContent = text ? this.previewWords(text) : note.dataset.empty || '';
      note.closest('[data-gift-card-note]')?.classList.toggle('is-empty', !text);
    }

    /**
     * The first few words, the way Liquid's `truncatewords` writes them.
     *
     * The same rule has to run in both places or the preview would change the
     * moment the section re-rendered - full while typing, short after a cart
     * change, which reads as a bug rather than a length.
     */
    previewWords(text) {
      const words = text.split(/\s+/);
      if (words.length <= PREVIEW_WORDS) return text;
      return words.slice(0, PREVIEW_WORDS).join(' ') + '…';
    }

    setBusy(busy) {
      this.pending += busy ? 1 : -1;
      this.classList.toggle('is-busy', this.pending > 0);
      const checkout = this.querySelector('[data-cart-checkout]');
      if (checkout) checkout.setAttribute('aria-busy', this.pending > 0 ? 'true' : 'false');
    }

    showError(message, row) {
      if (!message) return;

      const target = (row && row.querySelector('[data-cart-item-error]')) || this.errorTarget;
      if (!target) return;

      target.textContent = message;
      target.hidden = false;
    }

    clearErrors() {
      this.querySelectorAll('[data-cart-error], [data-cart-item-error]').forEach((element) => {
        element.textContent = '';
        element.hidden = true;
      });
    }
  }

  if (!customElements.get('lagift-cart')) {
    customElements.define('lagift-cart', LagiftCart);
  }
})();
